import { useEffect, useRef } from 'preact/hooks';
import { axPos } from '../core/wavelength';
import { fmtNum } from '../core/util';
import type { SignaturePoint } from '../render/sam';
import { classMean, type TrainingClass } from '../render/sam';

const rgb = (c: [number, number, number]) => `rgb(${c[0]},${c[1]},${c[2]})`;

/** Colours for plain comparison pins, distinct from any class palette. */
const PIN_COLOURS = ['#F2A93B', '#79C4A2', '#6FA8DC', '#E0614B', '#C58BE0', '#E8D44D'];

export interface Pin { label: string; pts: SignaturePoint[] }

/**
 * Reflectance against wavelength for one pixel. The fastest possible check on band
 * assignment: vegetation dips hard in red and jumps across the red edge, so a swapped
 * band 4/5 shows up as a broken curve rather than a subtle hue shift.
 */
export function SignaturePlot(
  { classes, pins, hover, active, onClear, onRemovePin }:
  { classes: TrainingClass[]; pins: Pin[]; hover: SignaturePoint[] | null;
    active: boolean; onClear: () => void; onRemovePin: (i: number) => void },
) {
  const cv = useRef<HTMLCanvasElement>(null);
  // Each class contributes its mean as a solid curve and its training points as faint
  // ones, so a class whose examples disagree is visible rather than merely reported.
  const series = [
    ...classes.flatMap((c) => {
      const mean = classMean(c.points);
      const members = c.points.map((pts) => ({ pts, colour: rgb(c.colour), dashed: true }));
      return mean ? [...members, { pts: mean, colour: rgb(c.colour), dashed: false }] : members;
    }),
    ...pins.map((p, i) => ({
      pts: p.pts, colour: PIN_COLOURS[i % PIN_COLOURS.length]!, dashed: false,
    })),
    ...(hover ? [{ pts: hover, colour: 'rgba(157,180,190,.85)', dashed: true }] : []),
  ];

  useEffect(() => {
    const c = cv.current;
    if (!c || !series.length) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const w = c.width, h = c.height, PAD = 26;
    ctx.clearRect(0, 0, w, h);
    const all = series.flatMap((s) => s.pts.map((p) => p.val));
    const vmin = Math.min(0, ...all), vmax = Math.max(...all) || 1;
    const span = (vmax - vmin) || 1;
    const X = (nm: number) => PAD + axPos(nm) * (w - PAD - 6);
    const Y = (v: number) => h - 18 - ((v - vmin) / span) * (h - 30);
    const css = getComputedStyle(document.documentElement);
    ctx.strokeStyle = css.getPropertyValue('--line2').trim() || '#31505C';
    ctx.beginPath(); ctx.moveTo(PAD, 4); ctx.lineTo(PAD, h - 18); ctx.lineTo(w - 4, h - 18); ctx.stroke();
    ctx.fillStyle = css.getPropertyValue('--ink2').trim() || '#9DB4BE';
    ctx.font = '9px monospace'; ctx.textAlign = 'center';
    [500, 700, 1000, 1600, 2200].forEach((nm) => {
      if (X(nm) < PAD) return;
      ctx.fillText(String(nm), X(nm), h - 6);
      ctx.strokeStyle = 'rgba(128,150,160,.16)';
      ctx.beginPath(); ctx.moveTo(X(nm), 4); ctx.lineTo(X(nm), h - 18); ctx.stroke();
    });
    ctx.textAlign = 'right';
    ctx.fillText(fmtNum(vmax), PAD - 3, 12);
    ctx.fillText(fmtNum(vmin), PAD - 3, h - 20);
    series.forEach((s) => {
      ctx.strokeStyle = s.colour; ctx.lineWidth = s.dashed ? 1 : 1.8;
      ctx.setLineDash(s.dashed ? [3, 3] : []);
      ctx.beginPath();
      s.pts.forEach((p, i) => {
        const x = X(p.nm), y = Y(p.val);
        if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y);
      });
      ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = s.colour;
      s.pts.forEach((p) => { ctx.beginPath(); ctx.arc(X(p.nm), Y(p.val), 2.4, 0, 7); ctx.fill(); });
    });
  });

  // The panel stays mounted whenever a raster is loaded, reporting how many curves it
  // holds. Vanishing entirely made it undiscoverable, and made "no data" and "not
  // rendering" indistinguishable from outside.
  if (!active) return null;
  return (
    <div class="ov sig" data-testid="signature" data-series={series.length}>
      <h3>Spectral signature
        {series.length > 0 && <button class="btn tiny" onClick={onClear}>clear</button>}
      </h3>
      {series.length === 0 && (
        <p class="sig-empty">
          <span class="no-touch">Hover the map to read a spectrum, click</span>
          <span class="touch-only">Tap the map</span> to pin one for comparison. In
          classification mode that trains the selected class instead.
        </p>
      )}
      <canvas ref={cv} width={330} height={150}
        style={series.length ? undefined : { display: 'none' }} />
      <div class="sig-legend">
        {classes.filter((c) => c.points.length).map((c) => (
          <span key={c.id}>
            <i style={{ background: rgb(c.colour) }} />{c.name} ({c.points.length})
          </span>
        ))}
        {pins.map((p, i) => (
          <span key={i} onClick={() => onRemovePin(i)} title="Click to remove">
            <i style={{ background: PIN_COLOURS[i % PIN_COLOURS.length] }} />{p.label}
          </span>
        ))}
      </div>
      <p class="sig-hint">Click the map to pin a point. Vegetation dips in red and jumps
        across the red edge — a wrong band order shows up here immediately.</p>
    </div>
  );
}
